import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import {
  PageHeader,
  SectionCard,
  FieldLabel,
  ApproverRoleBadge,
  WarningBanner,
} from "./shared/components";
import {
  getVigilanceOfficerConfig,
  saveVigilanceOfficerConfig,
} from "./services/eSeparation.service";

const padEmpNo = (v?: string | null): string => {
  const t = (v ?? "").trim();
  return t ? t.padStart(8, "0") : "";
};

const VigilanceConfig: React.FC = () => {
  const navigate = useNavigate();

  const [currentOfficer, setCurrentOfficer] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [empId, setEmpId] = useState("");
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    const res = await getVigilanceOfficerConfig();
    setCurrentOfficer(res.data?.officer_emp_id ?? null);
    setLoading(false);
  };

  useEffect(() => {
    load();
  }, []);

  const save = async () => {
    if (!empId.trim())
      return toast.warning("Employee ID is required.");
    setSaving(true);
    const res = await saveVigilanceOfficerConfig(padEmpNo(empId));
    setSaving(false);
    if (res.success) {
      toast.success("Vigilance officer assigned.");
      setEmpId("");
      load();
    } else {
      toast.error(res.message || "Failed to assign vigilance officer.");
    }
  };

  return (
    <div className="page-wrapper">
      <div className="content">
        <PageHeader
          title="Vigilance Officer"
          subtitle="Assign who receives resignations sent to Vigilance"
          right={<ApproverRoleBadge role="HR" label="HR Admin" />}
          onExit={() => navigate(-1)}
        />

        <WarningBanner
          type="info"
          message="Only one vigilance officer is active at a time. Reassigning replaces the current officer for all future 'Send to Vigilance' cases — cases already dispatched stay with the officer they were sent to."
        />

        <div className="row">
          <div className="col-lg-6">
            <SectionCard index={0} title="Current Vigilance Officer">
              {loading ? (
                <div className="text-center py-4 text-muted">
                  <div className="spinner-border text-primary mb-2" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                  <div className="small">Loading...</div>
                </div>
              ) : currentOfficer ? (
                <div
                  className="d-flex align-items-center gap-3 p-3"
                  style={{
                    background: "#F0E7F6",
                    border: "1px solid #4A1A6E22",
                    borderRadius: 12,
                  }}
                >
                  <span
                    className="d-inline-flex align-items-center justify-content-center flex-shrink-0"
                    style={{
                      width: 42,
                      height: 42,
                      borderRadius: 12,
                      background: "#fff",
                      color: "#4A1A6E",
                    }}
                  >
                    <i className="ti ti-shield-check" style={{ fontSize: 20 }} />
                  </span>
                  <div>
                    <div className="fw-semibold">{currentOfficer}</div>
                    <small className="text-muted">
                      Receives every "Send to Vigilance" case
                    </small>
                  </div>
                </div>
              ) : (
                <WarningBanner
                  type="warning"
                  message="No vigilance officer configured yet. HR cannot send resignations to Vigilance until one is assigned."
                />
              )}
            </SectionCard>
          </div>

          <div className="col-lg-6">
            <SectionCard index={1} title="Assign / Reassign Vigilance Officer">
              <FieldLabel label="Employee ID" required />
              <input
                className="form-control mb-3"
                inputMode="numeric"
                value={empId}
                onChange={(e) => setEmpId(e.target.value)}
                onBlur={(e) =>
                  setEmpId(e.target.value.trim() ? padEmpNo(e.target.value) : "")
                }
                placeholder="8-digit employee number"
              />
              <button
                className="btn btn-primary"
                onClick={save}
                disabled={saving}
              >
                <i className="ti ti-device-floppy me-1" />
                {saving ? "Saving..." : "Assign Officer"}
              </button>
            </SectionCard>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VigilanceConfig;
